<template>
  <form
    class="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm overflow-hidden"
    @submit.prevent="submit"
  >
    <!-- Textarea area -->
    <div class="p-3 pb-2">
      <textarea
        ref="textareaRef"
        v-model="comment"
        rows="2"
        class="w-full px-1 py-1 text-xs border-0 resize-none bg-transparent text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-0 placeholder:text-slate-400"
        :placeholder="placeholder"
        :disabled="loading"
        @input="autoGrow"
      ></textarea>
    </div>

  <div class="h-px bg-slate-100 dark:bg-slate-800"></div>

  <!-- File upload dropzone -->
  <div class="px-3 py-2.5 bg-slate-50/80 dark:bg-slate-800/40">
    <TicketFileUpload
      v-model="attachments"
      variant="dropzone"
      :hint="attachmentsHint"
      :dropzone-title="dropzoneTitle"
      :lang="lang"
      :disabled="loading"
      @error="onUploadError"
    />
  </div>

  <div class="h-px bg-slate-100 dark:bg-slate-800"></div>

  <!-- Footer actions -->
  <div class="flex items-center justify-end gap-2 px-3 py-2.5">
    <button
      type="submit"
      class="inline-flex items-center gap-1.5 px-5 py-2 text-xs font-semibold text-white rounded-xl bg-primary hover:bg-primary/90 shadow-sm shadow-primary/25 disabled:opacity-50 transition-all active:scale-[0.98]"
      :disabled="loading || !canSubmit"
    >
      <i v-if="loading" class="mdi mdi-loading mdi-spin text-[15px]"></i>
      <i v-else class="mdi mdi-send text-[15px]"></i>
      {{ submitLabel }}
    </button>
  </div>
  </form>
</template>

<script setup>
import { ref, computed, nextTick } from "vue";
import TicketFileUpload from "./TicketFileUpload.vue";

const props = defineProps({
  loading: { type: Boolean, default: false },
  placeholder: { type: String, default: "" },
  submitLabel: { type: String, default: "Send" },
  attachmentsHint: { type: String, default: "" },
  dropzoneTitle: { type: String, default: "" },
  lang: { type: String, default: "ar" },
});

const emit = defineEmits(["submit", "error"]);

const comment = ref("");
const attachments = ref([]);
const textareaRef = ref(null);

const canSubmit = computed(
  () => comment.value.trim().length > 0 || attachments.value.length > 0,
);

function autoGrow() {
  const el = textareaRef.value;
  if (!el) return;
  el.style.height = "auto";
  el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
}

function onUploadError(msg) {
  emit("error", msg);
}

function submit() {
  if (!canSubmit.value || props.loading) return;
  emit("submit", {
    comment: comment.value.trim(),
    attachments: [...attachments.value],
  });
  comment.value = "";
  attachments.value = [];
  nextTick(() => {
    if (textareaRef.value) textareaRef.value.style.height = "auto";
  });
}

function reset() {
  comment.value = "";
  attachments.value = [];
}

defineExpose({ reset });
</script>
